import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  ShieldCheck,
  Upload,
  XCircle,
} from "lucide-react";
import Button from "../../components/ui/Button";
import DataTable from "../../components/dashboard/DataTable";
import { usePortfolios } from "../../context/PortfolioContext";
import { enrichPortfolioGroup } from "../../services/portfolioGroupsApi";
import {
  PORTFOLIO_UPLOAD_COLUMNS,
  formatCurrency,
  formatPercent,
} from "../../data/mock/dashboardData";
import {
  downloadPortfolioTemplate,
  parsePortfolioFile,
  buildCleanedPortfolioFile,
  simulatePortfolioImport,
} from "../../services/portfolioUpload";
import { showError, showSuccess } from "../../utils/toast";

const STEPS = [
  { id: "upload", label: "Upload file" },
  { id: "validate", label: "Validate data" },
  { id: "import", label: "Import & create cases" },
];

const RULES = [
  { title: "One sheet, KYC order", detail: "Individual KYC → Loan KYC → Payment KYC in one header row." },
  { title: "One row per debt facility", detail: "Empty rows are skipped. The same Account Number may appear on more than one row." },
  {
    title: "Account Numbers",
    detail: "Each row becomes its own case with a generated case code. The Account Number is stored once on the customer.",
  },
  { title: "Ghana Card required", detail: "Format GHA-XXXXXXXXX-X." },
  { title: "Ghana mobile numbers", detail: "Phone number and mobile: 024XXXXXXX or 233XXXXXXXXX." },
  { title: "Location", detail: "Country, Region, and Exact Area are required for each debtor." },
  { title: "Loan & outstanding > 0", detail: "Loan Amount and Outstanding Balance must be greater than zero." },
  { title: "Dates required", detail: "DOB and Due Date: DD/MM/YYYY or YYYY-MM-DD." },
  { title: "Gender", detail: "Male, Female, or Other." },
  { title: "Group assignment", detail: "All rows are linked to this group — no portfolio column needed." },
];

function StepIndicator({ current }) {
  return (
    <ol className="flex flex-wrap items-center gap-2 sm:gap-0">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={step.id} className="flex items-center">
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                done
                  ? "bg-emerald-50 text-emerald-700"
                  : active
                    ? "bg-primary text-white shadow-md shadow-primary/20"
                    : "bg-primary/[0.05] text-primary/35"
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                  done ? "bg-emerald-500 text-white" : active ? "bg-white/20" : "bg-primary/10"
                }`}
              >
                {done ? "✓" : i + 1}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`mx-1 hidden h-px w-6 sm:block ${done ? "bg-emerald-300" : "bg-primary/10"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ValidationRing({ label, value, total, tone = "primary" }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const colors = {
    primary: "stroke-primary",
    emerald: "stroke-emerald-500",
    red: "stroke-red-500",
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-20 w-20">
        <svg className="-rotate-90" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="15.5" fill="none" className="stroke-primary/10" strokeWidth="3" />
          <circle
            cx="18"
            cy="18"
            r="15.5"
            fill="none"
            className={colors[tone]}
            strokeWidth="3"
            strokeDasharray={`${pct} 100`}
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-primary">
          {value}
        </span>
      </div>
      <p className="mt-2 text-[11px] font-medium text-primary/45">{label}</p>
    </div>
  );
}

export default function PortfolioUploadPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { getGroup, addUploadToGroup, ensureGroup, uploadFileToGroup, useLiveApi } = usePortfolios();
  const group = enrichPortfolioGroup(getGroup(groupId));

  useEffect(() => {
    if (!group && groupId) {
      ensureGroup(groupId).catch(() => {});
    }
  }, [group, groupId, ensureGroup]);

  const inputRef = useRef(null);
  const [step, setStep] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [previewTab, setPreviewTab] = useState("valid");
  const [importDone, setImportDone] = useState(null);

  const uploadBlocked = Boolean(group?.hasPortfolioData) && !importDone;

  useEffect(() => {
    if (!group || !uploadBlocked) return;
    showError("This group already has an upload. Create a new group to upload another file.");
  }, [group?.id, uploadBlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetValidation = useCallback(() => {
    setStep(0);
    setFile(null);
    setResult(null);
    setError("");
    setPreviewTab("valid");
    setImportDone(null);
  }, []);

  const processFile = async (nextFile) => {
    if (!nextFile || !group) return;
    const ext = nextFile.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setError("Please upload an Excel (.xlsx, .xls) or CSV file.");
      return;
    }
    if (nextFile.size > 5 * 1024 * 1024) {
      setError("File size must be under 5 MB.");
      return;
    }

    setError("");
    setParsing(true);
    setFile(nextFile);
    try {
      const parsed = await parsePortfolioFile(nextFile, { groupName: group.name });
      setResult(parsed);
      setStep(1);
      setPreviewTab(parsed.errorCount > 0 ? "errors" : "valid");
      if (parsed.errorCount === 0) {
        showSuccess(`Validation passed — ${parsed.validCount} accounts ready to import.`);
      } else {
        showError(
          `Validation failed — ${parsed.errorCount} row${parsed.errorCount === 1 ? "" : "s"} need fixing. Correct the Excel file and re-upload.`
        );
      }
    } catch (err) {
      setError(err.message || "Could not read the file.");
      setFile(null);
      showError(err.message || "Could not read the file.");
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!group || !result) return;
    if (result.errorCount > 0) {
      setError(
        `${result.errorCount} row${result.errorCount === 1 ? "" : "s"} failed validation. Fix them in Excel before uploading.`
      );
      setPreviewTab("errors");
      showError("Fix all validation errors in your Excel file before uploading.");
      return;
    }
    if (!result.validCount) {
      setError("No valid rows to upload.");
      showError("No valid rows to upload.");
      return;
    }
    if (useLiveApi && !file) return;

    setImporting(true);
    setError("");
    try {
      if (useLiveApi) {
        const cleanedFile = buildCleanedPortfolioFile(result, file?.name || result.fileName);
        const outcome = await uploadFileToGroup(group.id, cleanedFile);
        const upload = outcome?.upload;
        const summary = {
          uploadId: upload?.id,
          accounts: upload?.accounts ?? upload?.validationPass ?? result.validCount,
          totalOwed: upload?.totalOwed ?? result.totalAmount,
          passRate: upload?.validationPassRate ?? result.passRate,
          invalidRows: upload?.invalidRows ?? 0,
        };
        setImportDone(summary);
        setStep(2);
        showSuccess(
          `Uploaded cleaned file to ${group.name}. ${summary.accounts} account${
            summary.accounts === 1 ? "" : "s"
          } imported.`
        );
        return;
      }

      const summary = await simulatePortfolioImport(result);
      addUploadToGroup(group.id, {
        id: summary.uploadId,
        fileName: result.fileName,
        accounts: summary.accounts,
        totalOwed: summary.totalOwed,
        uploadedAt: new Date().toISOString().slice(0, 10),
        status: "completed",
        stage: "cases_created",
        casesCreated: summary.accounts,
        validationPassRate: summary.passRate,
        invalidRows: result.errorCount,
                        records: result.validRows.map((row, i) => ({
          id: `${summary.uploadId}-${String(i + 1).padStart(4, "0")}`,
          ...row,
          amount_owed: row.outstanding_balance ?? row.outstanding_amount,
          amount_paid: row.amount_paid ?? 0,
          case_id: `RC-${8800 + (i % 900)}`,
        })),
      });
      setImportDone(summary);
      setStep(2);
      showSuccess(`Imported ${summary.accounts} accounts into ${group.name}.`);
    } catch (err) {
      setError(err.message || "Could not upload the file.");
      showError(err.message || "Could not upload the file.");
    } finally {
      setImporting(false);
    }
  };

  if (!group) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-semibold text-primary">Group not found</p>
        <Link to="/dashboard/portfolios" className="mt-4 inline-block text-sm font-bold text-primary underline">
          Back to portfolios
        </Link>
      </div>
    );
  }

  if (uploadBlocked) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-semibold text-primary">This group already has an upload</p>
        <p className="mt-2 text-sm text-primary/55">
          One group accepts one file. Create a new group to upload another file.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/dashboard/portfolios"
            className="inline-flex items-center justify-center rounded-xl border border-primary/15 px-4 py-2.5 text-sm font-semibold text-primary"
          >
            Back to portfolios
          </Link>
          <Link
            to={`/dashboard/portfolios/${group.id}/uploads/${group.uploads?.[0]?.id || ""}`}
            className="inline-flex items-center justify-center rounded-xl bg-collect px-4 py-2.5 text-sm font-bold text-white"
          >
            View uploaded file
          </Link>
        </div>
      </div>
    );
  }

  const previewColumns =
    previewTab === "errors"
      ? [
          { key: "rowIndex", header: "Row", type: "number", align: "center" },
          { key: "account_number", header: "Account", type: "id" },
          ...[
            { key: "first_name", header: "First name" },
            { key: "last_name", header: "Last name" },
          ],
          {
            key: "errors",
            header: "Issues",
            type: "text",
            render: (row) => (
              <ul className="min-w-[200px] space-y-1">
                {row.errors?.map((e) => (
                  <li key={e} className="flex items-start gap-1.5 text-xs text-red-600">
                    <XCircle className="mt-0.5 h-3 w-3 shrink-0" />
                    {e}
                  </li>
                ))}
              </ul>
            ),
          },
        ]
      : [
          ...PORTFOLIO_UPLOAD_COLUMNS.map((c) => ({
            key: c.key,
            header: c.label,
            type:
              c.key === "outstanding_balance" ||
              c.key === "loan_amount" ||
              c.key === "amount_paid"
                ? "currency"
                : c.key === "phone" || c.key === "customer_number"
                  ? "phone"
                  : c.key === "customer_name"
                    ? "name"
                    : c.key === "account_number" || c.key === "ghana_card_number"
                      ? "id"
                      : c.key === "due_date" ||
                          c.key === "date_of_birth" ||
                          c.key === "last_payment_date"
                        ? "date"
                        : "text",
          })),
        ];

  const previewData =
    previewTab === "errors"
      ? (result?.errorRows ?? []).map((r, i) => ({ ...r, id: `err-${i}` }))
      : (result?.validRows ?? []).map((r, i) => ({
          ...r,
          id: `ok-${i}`,
          amount_owed: r.outstanding_balance ?? r.outstanding_amount,
        }));

  return (
    <div className="min-h-full bg-[#faf8f6]">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-primary/[0.08] bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-8xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to="/dashboard/portfolios"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/10 text-primary/60 transition-colors hover:bg-primary/[0.04] hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/35">
                Upload to group
              </p>
              <h1 className="truncate text-lg font-bold text-primary">{group.name}</h1>
            </div>
          </div>
          <StepIndicator current={step} />
        </div>
      </div>

      <div className="mx-auto max-w-8xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Required columns — full width */}
        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-primary/[0.07] bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-primary">Required columns</p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {PORTFOLIO_UPLOAD_COLUMNS.filter((c) => c.required).map((c) => (
                <span
                  key={c.key}
                  className="rounded-lg bg-primary/[0.06] px-2.5 py-1 text-[11px] font-semibold text-primary/75 ring-1 ring-primary/[0.06]"
                >
                  {c.label}
                </span>
              ))}
              {PORTFOLIO_UPLOAD_COLUMNS.filter((c) => !c.required).map((c) => (
                <span
                  key={c.key}
                  className="rounded-lg bg-primary/[0.02] px-2.5 py-1 text-[11px] font-medium text-primary/40 ring-1 ring-primary/[0.05]"
                >
                  {c.label} <span className="text-primary/30">(optional)</span>
                </span>
              ))}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 sm:self-center"
            onClick={() => downloadPortfolioTemplate(group.name)}
          >
            <Download className="h-4 w-4" />
            Download template
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-12 lg:items-start">
          {/* Sidebar — validation rules */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="overflow-hidden rounded-2xl border border-primary/[0.07] bg-white shadow-sm">
              <div className="bg-gradient-to-br from-primary to-[#EF4444] px-5 py-4">
                <div className="flex items-center gap-2 text-white/80">
                  <ShieldCheck className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wider">Validation rules</span>
                </div>
                <p className="mt-2 text-sm text-white/70">
                  Every row is validated before import.
                </p>
              </div>
              <ul className="divide-y divide-primary/[0.05] px-5 py-2">
                {RULES.map((rule) => (
                  <li key={rule.title} className="py-3">
                    <p className="text-sm font-semibold text-primary">{rule.title}</p>
                    <p className="mt-0.5 text-xs text-primary/45">{rule.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main workspace */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="rounded-2xl border border-primary/[0.07] bg-white p-6 shadow-sm sm:p-8"
                >
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragging(false);
                      processFile(e.dataTransfer.files?.[0]);
                    }}
                    className={`relative overflow-hidden rounded-2xl border-2 border-dashed transition-all ${
                      dragging
                        ? "border-accent bg-accent/5 scale-[1.01]"
                        : parsing
                          ? "border-primary/20 bg-primary/[0.02]"
                          : "border-primary/15 bg-gradient-to-br from-primary/[0.02] to-accent/[0.04]"
                    }`}
                  >
                    <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-accent/10 blur-2xl" />
                    <div className="relative px-6 py-16 text-center sm:py-20">
                      {parsing ? (
                        <>
                          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
                          <p className="mt-4 text-base font-bold text-primary">Validating your file…</p>
                          <p className="mt-1 text-sm text-primary/45">Checking every row against the rules</p>
                        </>
                      ) : (
                        <>
                          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary shadow-lg shadow-primary/25">
                            <FileSpreadsheet className="h-8 w-8 text-white" />
                          </div>
                          <p className="mt-5 text-lg font-bold text-primary">
                            Drop Excel file here
                          </p>
                          <p className="mt-2 text-sm text-primary/45">
                            .xlsx, .xls or .csv · max 5 MB · assigned to{" "}
                            <span className="font-semibold text-primary">{group.name}</span>
                          </p>
                          <input
                            ref={inputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="hidden"
                            onChange={(e) => processFile(e.target.files?.[0])}
                          />
                          <Button
                            className="mt-6"
                            size="md"
                            onClick={() => inputRef.current?.click()}
                          >
                            <Upload className="h-4 w-4" />
                            Choose file from computer
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {error && (
                    <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">Upload failed</p>
                        <p className="mt-0.5 text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {step === 1 && result && (
                <motion.div
                  key="validate"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="space-y-5"
                >
                  {/* File chip + stats */}
                  <div className="rounded-2xl border border-primary/[0.07] bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/[0.08]">
                          <FileSpreadsheet className="h-5 w-5 text-primary" />
                        </span>
                        <div>
                          <p className="font-bold text-primary">{result.fileName}</p>
                          <p className="text-xs text-primary/40">
                            {formatCurrency(result.totalAmount)} total outstanding in valid rows
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={resetValidation}
                        className="text-xs font-semibold text-primary/50 hover:text-primary"
                      >
                        Upload different file
                      </button>
                    </div>

                    <div className="mt-6 flex justify-around border-t border-primary/[0.06] pt-6">
                      <ValidationRing label="Total rows" value={result.totalRows} total={result.totalRows} />
                      <ValidationRing
                        label="Valid"
                        value={result.validCount}
                        total={result.totalRows}
                        tone="emerald"
                      />
                      <ValidationRing
                        label="Errors"
                        value={result.errorCount}
                        total={result.totalRows}
                        tone="red"
                      />
                    </div>

                    <p className="mt-4 text-center text-sm text-primary/50">
                      Validation pass rate:{" "}
                      <span className="font-bold text-primary">{formatPercent(result.passRate)}</span>
                    </p>
                  </div>

                  {/* Preview tabs */}
                  <div className="rounded-2xl border border-primary/[0.07] bg-white shadow-sm">
                    <div className="flex items-center justify-between border-b border-primary/[0.06] px-5 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setPreviewTab("valid")}
                          className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                            previewTab === "valid"
                              ? "bg-emerald-600 text-white"
                              : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          }`}
                        >
                          Valid rows ({result.validCount})
                        </button>
                        {result.errorCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setPreviewTab("errors")}
                            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                              previewTab === "errors"
                                ? "bg-red-600 text-white"
                                : "bg-red-50 text-red-700 hover:bg-red-100"
                            }`}
                          >
                            Errors ({result.errorCount})
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <DataTable
                        columns={previewColumns}
                        data={previewData}
                        pageSize={10}
                        emptyMessage="No rows in this view."
                      />
                    </div>
                  </div>

                  {result.errorCount > 0 && (
                    <div className="rounded-xl border border-red-200/80 bg-red-50/80 px-4 py-3 text-sm text-red-900">
                      <p className="font-semibold">
                        Upload blocked — {result.errorCount} row
                        {result.errorCount !== 1 ? "s" : ""} failed validation.
                      </p>
                      <p className="mt-1 text-red-800/80">
                        Review the Errors tab, correct the issues in your Excel file, then re-upload.
                        The file will not be sent to the server until every row passes.
                      </p>
                    </div>
                  )}

                  {error && (
                    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Sticky action bar */}
                  <div className="sticky bottom-4 flex flex-nowrap items-center justify-between gap-3 overflow-x-auto rounded-2xl border border-primary/10 bg-white/95 px-5 py-4 shadow-lg backdrop-blur-sm">
                    <p className="min-w-0 shrink text-sm text-primary/50">
                      {result.errorCount > 0
                        ? "Fix Excel errors before import is enabled."
                        : useLiveApi
                          ? "Send cleaned data to import accounts under "
                          : "Ready to create "}
                      {result.errorCount === 0 && !useLiveApi ? (
                        <span className="font-bold text-primary">{result.validCount}</span>
                      ) : null}
                      {result.errorCount === 0 && !useLiveApi ? " recovery cases under " : null}
                      {result.errorCount === 0 ? (
                        <span className="font-bold text-primary">{group.name}</span>
                      ) : null}
                    </p>
                    <div className="flex shrink-0 flex-nowrap gap-2">
                      <Button variant="outline" size="sm" onClick={resetValidation}>
                        Re-upload
                      </Button>
                      <Button
                        size="sm"
                        loading={importing}
                        disabled={
                          result.errorCount > 0 ||
                          !result.validCount ||
                          (useLiveApi && !file)
                        }
                        onClick={handleImport}
                      >
                        {useLiveApi ? "Upload cleaned file" : `Import ${result.validCount} accounts`}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && importDone && (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="rounded-2xl border border-emerald-200/60 bg-white p-10 text-center shadow-sm"
                >
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/80">
                    <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                  </div>
                  <h2 className="mt-6 text-2xl font-bold text-primary">
                    {useLiveApi ? "Upload complete" : "Import complete"}
                  </h2>
                  <p className="mx-auto mt-2 max-w-md text-sm text-primary/50">
                    {useLiveApi
                      ? `${importDone.accounts} valid row${importDone.accounts === 1 ? "" : "s"} imported under `
                      : `${importDone.accounts} recovery cases created under `}
                    <span className="font-semibold text-primary">{group.name}</span>
                    {useLiveApi && importDone.invalidRows
                      ? `. ${importDone.invalidRows} invalid row${
                          importDone.invalidRows === 1 ? "" : "s"
                        } were skipped.`
                      : useLiveApi
                        ? "."
                        : " with complete audit trail."}
                  </p>
                  <p className="mt-3 text-xl font-bold text-primary">
                    {formatCurrency(importDone.totalOwed)} total outstanding
                  </p>
                  <div className="mt-8 flex flex-nowrap justify-center gap-3 overflow-x-auto">
                    {!useLiveApi && importDone.uploadId ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/dashboard/portfolios/${group.id}/uploads/${importDone.uploadId}`)
                        }
                      >
                        View accounts
                      </Button>
                    ) : null}
                    <Button size="sm" onClick={() => navigate("/dashboard/portfolios")}>
                      Back to groups
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
